import classNames from 'classnames';
import React, { PureComponent } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import colors from '@spotify-internal/encore-foundation';
import { formatPercentage } from '../../../../lib/Formatter';
import chartStyles from 'styles/charts.module.scss';
import { GenderWithPercentage } from '../../../../types';
import { injectIntl, IntlShape, defineMessages } from 'react-intl';

const GENDERS = ['female', 'male', 'notSpecified', 'nonBinary'];

const COLORS_BY_GENDER = {
  female: '#5a3f5a',
  male: '#bca5cf',
  notSpecified: '#ffd3d7',
  nonBinary: '#aacad8',
};

const genderMessages = defineMessages({
  female: {
    id: 'network.audience.gender.female',
    defaultMessage: 'female',
  },
  male: {
    id: 'network.audience.gender.male',
    defaultMessage: 'male',
  },
  notSpecified: {
    id: 'network.audience.gender.not-specified',
    defaultMessage: 'not specified',
  },
  nonBinary: {
    id: 'network.audience.gender.non-binary',
    defaultMessage: 'non-binary',
  },
});

type Props = {
  genderData: GenderWithPercentage[];
  intl: IntlShape;
};

class InternationalizedGenderDemographicsChart extends PureComponent<Props> {
  FORMATTED_GENDER = {
    female: this.props.intl.formatMessage(genderMessages.female),
    male: this.props.intl.formatMessage(genderMessages.male),
    notSpecified: this.props.intl.formatMessage(genderMessages.notSpecified),
    nonBinary: this.props.intl.formatMessage(genderMessages.nonBinary),
  };

  valueAccessor = /* istanbul ignore next */ value =>
    formatPercentage(value.percentage, this.props.intl);

  tickFormatter = /* istanbul ignore next */ gender => this.FORMATTED_GENDER[gender].toUpperCase();

  renderBars = () =>
    this.props.genderData.map(({ gender }) => (
      <Cell key={`${gender}`} fill={COLORS_BY_GENDER[gender]} />
    ));

  renderLegend = () => (
    <Legend
      content={
        /* istanbul ignore next */ () => (
          <ul className={chartStyles.legend}>
            {GENDERS.map(gender => (
              <li key={gender} className={chartStyles['legend-item']}>
                <div
                  className={chartStyles.circle}
                  style={{ backgroundColor: COLORS_BY_GENDER[gender] }}
                />
                <span>{this.FORMATTED_GENDER[gender]}</span>
              </li>
            ))}
          </ul>
        )
      }
      wrapperStyle={{
        top: '-46px',
        textTransform: 'uppercase',
        color: colors['grey-40'],
      }}
    />
  );

  render() {
    return (
      <div data-qa="gender-demographics-chart" style={{ width: '100%', height: '350px' }}>
        <ResponsiveContainer height={350}>
          <BarChart
            data={this.props.genderData}
            margin={{ top: 25, right: 0, left: -20, bottom: 0 }}
            className={classNames(chartStyles.chart, chartStyles['bar-chart'])}
          >
            {this.renderLegend()}
            <XAxis
              axisLine={false}
              tickLine={false}
              dataKey="gender"
              tickFormatter={this.tickFormatter}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={value => formatPercentage(value, this.props.intl)}
              ticks={[0, 50, 100]}
            />
            <CartesianGrid vertical={false} />
            <Bar
              dataKey="percentage"
              fill="#bca5cf"
              minPointSize={2}
              stackId="percentStack"
              isAnimationActive={false}
              background={{ fill: 'rgba(241,241,241,.7)' }}
            >
              {this.renderBars()}
              <LabelList position="top" dataKey={this.valueAccessor} offset={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

const GenderDemographicsChart = injectIntl(InternationalizedGenderDemographicsChart);
export { GenderDemographicsChart };
